// eslint-disable-next-line no-restricted-syntax
import React from 'react';
import styled from 'styled-components';

import {i18n} from '../../../libs/shared/core/src/helpers/internationalization/i18n';
import {defineMessage, Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {Dictionary, isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  BetaFeaturePill,
  LegacyFeaturePill,
  NewFeaturePill,
} from '../../../libs/shared/ui/src/components/pill/featurePill';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsMultiGraphMetricsEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../models/api/analyticsMetricsModel';
import {helpCenterUrl} from '../../store/stateless/navigationActions';
import {
  AnalyticsEducationTooltip,
  isComplexAnalyticsTooltip,
} from '../components/reportElements/tooltips/analyticsTooltips';
import {
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedProgressMetricsEnum,
} from '../transformers/analyticsMetricsTransformersTypes';
import {
  activeTeammatesTooltip,
  firstReplyTimeTooltip,
  handleTimeTooltip,
  ignoredTooltip,
  messagesReceivedTooltip,
  messagesSentTooltip,
  newConversationsTooltip,
  replyTimeTooltip,
  stageRepliesToResolutionTooltip,
  stageResolutionTimeTooltip,
  stageResolvedFirstReplyTooltip,
  stageResolvedTooltip,
  stageUnresolvedTooltip,
  ticketRepliesToResolutionTooltip,
  ticketResolutionTimeTooltip,
  ticketResolvedFirstReplyTooltip,
  ticketResolvedTooltip,
  ticketUnresolvedTooltip,
} from './metricTooltips';
import {
  conversationStagesHelpCenterUrl,
  workloadReportHelpCenterUrl,
} from './reportHelpers/analyticsReportHelpers';
import {AnalyticsFeatureStatusesEnum} from './reportHelpers/reportTypes';

export interface MetricLabels<T extends AnyMetricKey | string = string> {
  // TODO this should be a MessageDescriptor and we should wrap it with usei18n when needed, directly in the components.
  // see https://github.com/frontapp/front-client/blob/master/docs/localization.md#outside-of-react-components
  // PB-40701
  label: string;
  tooltip?: AnalyticsEducationTooltip<T>;
  featureStatus?: AnalyticsFeatureStatusesEnum;
}

export type AnyMetricKey =
  | AnalyticsMetricsKeys
  | AnalyticsComputedGraphMetricsEnum
  | AnalyticsComputedProgressMetricsEnum;

export interface MetricWithLabel<ID extends AnyMetricKey> extends MetricLabels<ID> {
  id: ID;

  // For tables
  columns?: Dictionary<MetricLabels<string>>;
  // For multi-graphs
  series?: Array<MetricLabels>;
}

// Only use this function to validate the map of columns labels to ensure tooltip.metricId === key
function validateColumnsTooltipType<
  T extends {
    [K in keyof T]: K extends string ? MetricLabels<K> : never;
  },
>(value: T): T {
  return value;
}

/**
 * Label and tooltip content for each metric.
 */
const metricsLabels: {
  [K in AnyMetricKey]?: MetricWithLabel<K>;
} = {
  // Scalar
  // @TODO add missing metrics
  [AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-active-segments',
        message: 'Active conversations',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-active-segments-title">Active conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-active-segments">
          Conversation segments with at least one activity during this period (tag, comment, assign, ...).
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
      metricId: AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES]: {
    id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-active-teammates',
        message: 'Active teammates',
      }),
    ),
    tooltip: {
      ...activeTeammatesTooltip,
      metricId: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
    },
  },
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-archived-segments',
        message: 'Archived conversations',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-archived-segments-title">Archived conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-archived-segments">
          Conversation segments archived or snoozed this period, remaining archived at the end of the period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
      metricId: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY]: {
    id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-archived-segments-with-reply',
        message: 'Archived with reply',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-num-archived-segments-with-reply-title">
          Archived with reply
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-archived-segments-with-reply">
          Conversations archived or snoozed this period that remained archived at the end of the period and
          had at least one prior reply.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
      metricId: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-avg-first-reply-time',
        message: 'First reply time (avg)',
      }),
    ),
    tooltip: {
      ...firstReplyTimeTooltip,
      metricId: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-avg-handle-time',
        message: 'Handle time (avg)',
      }),
    ),
    tooltip: {
      ...handleTimeTooltip,
      metricId: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-avg-total-reply-time',
        message: 'Total reply time (avg)',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-total-reply-time-title">Total reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-total-reply-time">
          Cumulative time elapsed between receiving and replying to messages (average per conversation).
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc/total-reply-time-avg`,
      metricId: AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED]: {
    id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-messages-received',
        message: 'Messages received',
      }),
    ),
    tooltip: {
      ...messagesReceivedTooltip,
      metricId: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
    },
  },
  [AnalyticsScalarMetricsEnum.MESSAGES_SENT]: {
    id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-messages-sent',
        message: 'Messages sent',
      }),
    ),
    tooltip: {
      ...messagesSentTooltip,
      metricId: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
    },
  },
  [AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT]: {
    id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-new-segments',
        message: 'New conversations',
      }),
    ),
    tooltip: {
      ...newConversationsTooltip,
      metricId: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-avg-response-time',
        message: 'Reply time (avg)',
      }),
    ),
    tooltip: {
      ...replyTimeTooltip,
      metricId: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED]: {
    id: AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-pct-sla-achievement',
        message: 'SLA achievement (%)',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-pct-sla-achievement-title">SLA achievement (%)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-pct-sla-achieved">
          Percentage of messages with SLA conditions that did not end up in breach.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
      metricId: AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED,
    },
  },
  [AnalyticsComputedProgressMetricsEnum.STAGE_RESOLVED_SEGMENTS_PROGRESS]: {
    id: AnalyticsComputedProgressMetricsEnum.STAGE_RESOLVED_SEGMENTS_PROGRESS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-overview-pct-resolved-stages-title',
        message: 'Conversation stages',
      }),
    ),
    tooltip: {
      title: <Trans id="overview-pct-resolved-stages-tooltip-title">Conversation stages</Trans>,
      tooltipContent: (
        <Trans id="overview-pct-resolved-stages-tooltip">
          Percentage of resolved and unresolved conversations during the selected time period.
        </Trans>
      ),
      learnMoreUrl: conversationStagesHelpCenterUrl,
      metricId: AnalyticsComputedProgressMetricsEnum.STAGE_RESOLVED_SEGMENTS_PROGRESS,
    },
  },
  [AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER]: {
    id: AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-sla-trigger',
        message: 'Messages with SLA',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-trigger-title">Messages with SLA</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-trigger">
          Number of messages that met SLA conditions this period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/35hk47r`,
      metricId: AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER,
    },
  },
  [AnalyticsScalarMetricsEnum.SLA_BREACHES]: {
    id: AnalyticsScalarMetricsEnum.SLA_BREACHES,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-sla-breaches',
        message: 'SLA breaches',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-breaches-title">SLA breaches</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-breaches">
          Number of times you missed your SLA commitment by not replying to a message in time.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
      metricId: AnalyticsScalarMetricsEnum.SLA_BREACHES,
    },
  },
  [AnalyticsScalarMetricsEnum.SLA_WARNINGS]: {
    id: AnalyticsScalarMetricsEnum.SLA_WARNINGS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-sla-warnings',
        message: 'SLA warnings',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-warnings-title">SLA warnings</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-warnings">
          Number of times an SLA warning tag was applied this period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
      metricId: AnalyticsScalarMetricsEnum.SLA_WARNINGS,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-avg-sla-breach-time',
        message: 'SLA breach time (avg)',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-sla-breach-time-title">SLA breach time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-sla-breach-time">
          Time from the SLA breach to the next reply (average).
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hlqrj/sla-breach-time-avg`,
      metricId: AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.STAGE_NUM_IGNORED_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.STAGE_NUM_IGNORED_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-ignored-segments',
        message: 'Ignored conversations',
      }),
    ),
    tooltip: {
      ...ignoredTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_NUM_IGNORED_SEGMENTS,
    },
  },

  [AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-resolved-segments',
        message: 'Resolved conversations',
      }),
    ),
    tooltip: {
      ...stageResolvedTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-stage-unresolved-active-segments',
        message: 'Unresolved conversations',
      }),
    ),
    tooltip: {
      ...stageUnresolvedTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME]: {
    id: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-stage-avg-resolution-time',
        message: 'Resolution time (avg)',
      }),
    ),
    tooltip: {
      ...stageResolutionTimeTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY]: {
    id: AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-pct-resolved-on-first-reply',
        message: 'Resolved on first reply (%)',
      }),
    ),
    tooltip: {
      ...stageResolvedFirstReplyTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY,
    },
  },
  [AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION]: {
    id: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-stage-avg-replies-to-resolution',
        message: 'Replies to resolution (avg)',
      }),
    ),
    tooltip: {
      ...stageRepliesToResolutionTooltip,
      metricId: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION,
    },
  },
  [AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-resolved-segments',
        message: 'Resolved conversations',
      }),
    ),
    tooltip: {
      ...ticketResolvedTooltip,
      metricId: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-ticket-unresolved-active-segments',
        message: 'Unresolved conversations',
      }),
    ),
    tooltip: {
      ...ticketUnresolvedTooltip,
      metricId: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
    },
  },
  [AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME]: {
    id: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-ticket-avg-resolution-time',
        message: 'Resolution time (avg)',
      }),
    ),
    tooltip: {
      ...ticketResolutionTimeTooltip,
      metricId: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
    },
  },
  [AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY]: {
    id: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-pct-resolved-on-first-reply',
        message: 'Resolved on first reply (%)',
      }),
    ),
    tooltip: {
      ...ticketResolvedFirstReplyTooltip,
      metricId: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
    },
  },
  [AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION]: {
    id: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-ticket-avg-replies-to-resolution',
        message: 'Replies to resolution (avg)',
      }),
    ),
    tooltip: {
      ...ticketRepliesToResolutionTooltip,
      metricId: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
    },
  },
  // Graph
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-archived-segments-graph-label',
        message: 'Archived conversations',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-archived-segments-graph-tooltip-title">Archived conversations</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-archived-segments-graph-tooltip">
          Conversation segments archived or snoozed this period, remaining archived at the end of the period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
      metricId: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-archived-segments-with-reply-graph-label',
        message: 'Archived with reply',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-archived-segments-with-reply-graph-tooltip-title">
          Archived with reply
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-archived-segments-with-reply-graph-tooltip">
          Conversations archived or snoozed this period that remained archived at the end of the period and
          had at least one prior reply.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
      metricId: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-csat-survey-response-graph-label',
        message: 'Satisfaction score (avg)',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-csat-survey-response-graph-tooltip-title">Satisfaction score (avg)</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-csat-survey-response-graph-tooltip">
          Average score of survey responses this period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hk3qx`,
      metricId: AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-csat-survey-satisfaction-graph-label',
        message: 'Satisfaction score (%)',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-csat-survey-satisfaction-graph-tooltip-title">
          Satisfaction score (%)
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-csat-survey-satisfaction-graph-tooltip">
          Percent of surveys responded to this period with 4 or 5 stars.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
      metricId: AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH,
    },
  },
  [AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION]: {
    id: AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-pct-csat-survey-satisfaction-label',
        message: 'Overall Satisfaction',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-pct-csat-survey-satisfaction-tooltip-title">Overall Satisfaction</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-pct-csat-survey-satisfaction-tooltip">
          Percent of surveys responded to this period with 4 or 5 stars.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
      metricId: AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION,
    },
  },
  [AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-first-response-graph-label',
        message: 'First reply time (avg)',
      }),
    ),
    tooltip: {
      ...firstReplyTimeTooltip,
      metricId: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-handle-time-graph-label',
        message: 'Handle time (avg)',
      }),
    ),
    tooltip: {
      ...handleTimeTooltip,
      metricId: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-messages-received-graph-label',
        message: 'Messages received',
      }),
    ),
    tooltip: {
      ...messagesReceivedTooltip,
      metricId: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-new-conversations-graph-label',
        message: 'New conversations',
      }),
    ),
    tooltip: {
      ...newConversationsTooltip,
      metricId: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-messages-sent-graph-label',
        message: 'Messages sent',
      }),
    ),
    tooltip: {
      ...messagesSentTooltip,
      metricId: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_ACTIVE_TEAMMATES_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_ACTIVE_TEAMMATES_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analyics-metric-num-active-teammates-graph-label',
        message: 'Active teammates',
      }),
    ),
    tooltip: {
      ...activeTeammatesTooltip,
      metricId: AnalyticsGraphMetricsEnum.NUM_ACTIVE_TEAMMATES_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-sla-breach-graph-label',
        message: 'SLA breaches',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-num-sla-breach-graph-tooltip-title">SLA breaches</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-num-sla-breach-graph-tooltip">
          Number of times you missed your SLA commitment by not replying to a message in time.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
      metricId: AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-num-sla-warning-graph-label',
        message: 'SLA warnings',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-num-sla-warning-graph-tooltip-title">SLA warnings</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-num-sla-warning-graph-tooltip">
          Number of times an SLA warning tag was applied.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
      metricId: AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-pct-sla-achievement-graph-label',
        message: 'SLA achievement (%)',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-pct-sla-achievement-graph-tooltip-title">SLA achievement (%)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-pct-sla-achievement-graph-tooltip">
          Percentage of messages with SLA conditions that did not end up in breach.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
      metricId: AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-response-graph-label',
        message: 'Reply time (avg)',
      }),
    ),
    tooltip: {
      ...replyTimeTooltip,
      metricId: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-num-resolved-graph-label',
        message: 'Resolved conversations',
      }),
    ),
    tooltip: {
      ...stageResolvedTooltip,
      metricId: AnalyticsGraphMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-num-unresolved-active-graph-label',
        message: 'Unresolved conversations',
      }),
    ),
    tooltip: {
      ...stageUnresolvedTooltip,
      metricId: AnalyticsGraphMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-avg-resolution-time-graph-label',
        message: 'Resolution time (avg)',
      }),
    ),
    tooltip: {
      ...stageResolutionTimeTooltip,
      metricId: AnalyticsGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-pct-resolved-on-first-reply-label',
        message: 'Resolved on first reply (%)',
      }),
    ),
    tooltip: {
      ...stageResolvedFirstReplyTooltip,
      metricId: AnalyticsGraphMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-stage-avg-replies-to-resolution-graph-label',
        message: 'Replies to resolution (avg)',
      }),
    ),
    tooltip: {
      ...stageRepliesToResolutionTooltip,
      metricId: AnalyticsGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-num-resolved-graph-label',
        message: 'Resolved conversations',
      }),
    ),
    tooltip: {
      ...ticketResolvedTooltip,
      metricId: AnalyticsGraphMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-num-unresolved-active-graph-label',
        message: 'Unresolved conversations',
      }),
    ),
    tooltip: {
      ...ticketUnresolvedTooltip,
      metricId: AnalyticsGraphMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-avg-resolution-time-graph-label',
        message: 'Resolution time (avg)',
      }),
    ),
    tooltip: {
      ...ticketResolutionTimeTooltip,
      metricId: AnalyticsGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-pct-resolved-on-first-reply-label',
        message: 'Resolved on first reply (%)',
      }),
    ),
    tooltip: {
      ...ticketResolvedFirstReplyTooltip,
      metricId: AnalyticsGraphMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY_GRAPH,
    },
  },
  [AnalyticsGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-ticket-avg-replies-to-resolution-graph-label',
        message: 'Replies to resolution (avg)',
      }),
    ),
    tooltip: {
      ...ticketRepliesToResolutionTooltip,
      metricId: AnalyticsGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    },
  },
  // Heat map
  [AnalyticsHeatMapMetricsEnum.INBOX]: {
    id: AnalyticsHeatMapMetricsEnum.INBOX,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-inbox-heat-map-title',
        message: 'Busiest times',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-inbox-heat-map-tooltip-title">Busiest times</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-inbox-heat-map-tooltip">
          Busiest times for your team to help your staffing decisions. The darker the rectangle, the busier
          your team is.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}t/y4hz4m0/team-performance-report#busiest-times-heatmap`,
      metricId: AnalyticsHeatMapMetricsEnum.INBOX,
    },
  },
  [AnalyticsHeatMapMetricsEnum.INBOX_WITH_TICKETING]: {
    id: AnalyticsHeatMapMetricsEnum.INBOX_WITH_TICKETING,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-inbox-heat-map-with-ticketing-title',
        message: 'Busiest times',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-inbox-heat-map-with-ticketing-tooltip-title">Busiest times</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-inbox-heat-map-with-ticketing-tooltip">
          Busiest times for your team to help your staffing decisions. The darker the rectangle, the busier
          your team is.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}t/y4hz4m0/team-performance-report#busiest-times-heatmap`,
      metricId: AnalyticsHeatMapMetricsEnum.INBOX_WITH_TICKETING,
    },
  },
  // Used in the heat map
  [AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-inbox-heat-map-num-new-segments-label',
        message: 'New conversations',
      }),
    ),
    tooltip: {
      ...newConversationsTooltip,
      metricId: AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS,
    },
  },
  // Multi graphs
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_GRAPH]: {
    id: AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-inbox-graph-label',
        message: 'Workload by inbox',
      }),
    ),
  },
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_GRAPH]: {
    id: AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-tag-graph-label',
        message: 'Workload by tag',
      }),
    ),
  },
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_WITH_TICKETING_GRAPH]: {
    id: AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_WITH_TICKETING_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-inbox-with-ticketing-graph-label',
        message: 'Workload by inbox',
      }),
    ),
  },
  [AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_WITH_TICKETING_GRAPH]: {
    id: AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_WITH_TICKETING_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-tag-with-ticketing-graph-label',
        message: 'Workload by tag',
      }),
    ),
  },
  // Graphs
  [AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-response-time-graph-title',
        message: 'Reply time',
      }),
    ),
    tooltip: {
      ...replyTimeTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-first-reply-time-graph-title',
        message: 'First reply time',
      }),
    ),
    tooltip: {
      ...firstReplyTimeTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-stage-avg-resolution-time-graph-title',
        message: 'Resolution time',
      }),
    ),
    tooltip: {
      ...stageResolutionTimeTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.STAGE_AVG_RESOLUTION_TIME_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-stage-replies-to-resolution-graph-title',
        message: 'Replies to resolution',
      }),
    ),
    tooltip: {
      ...stageRepliesToResolutionTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-ticket-avg-resolution-time-graph-title',
        message: 'Resolution time',
      }),
    ),
    tooltip: {
      ...ticketResolutionTimeTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.TICKET_AVG_RESOLUTION_TIME_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-overview-report-ticket-replies-to-resolution-graph-title',
        message: 'Replies to resolution',
      }),
    ),
    tooltip: {
      ...ticketRepliesToResolutionTooltip,
      metricId: AnalyticsComputedGraphMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_GRAPH,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-what-happened-table-title',
        message: 'What happened',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-what-happened-table-tooltip-title">What happened chart</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-what-happened-table-tooltip">
          Conversations that are new, closed, and remain open in your backlog each 24 hr day.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#what-happened-chart`,
      metricId: AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH,
    },
    series: [
      {
        label: i18n._(
          defineMessage({
            id: 'analytics-new-workload',
            message: 'New workload',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-new-segments-tooltip-title">New workload</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-new-segments-tooltip">
              Conversations that were new, reopened, or moved into inboxes.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlq22/new-workload`,
          metricId: 'new_segments',
        },
      },
      {
        label: i18n._(
          defineMessage({
            id: 'analytics-closed-workload',
            message: 'Closed workload',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-closed-segments-tooltip-title">
              Closed workload
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-closed-segments-tooltip">
              Conversations archived or moved out of inboxes.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqbh/closed-workload`,
          metricId: 'closed_segments',
        },
      },
      {
        label: i18n._(
          defineMessage({
            id: 'analytics-open-at-end',
            message: 'Open at end',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-segments-remaining-open-tooltip-title">
              Open at end
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-segments-remaining-open-tooltip">
              Conversations still open at the end of the period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqjs`,
          metricId: 'remaining_open',
        },
      },
    ],
  },
  // Table
  [AnalyticsTableMetricsEnum.TEAM_CHANGES]: {
    id: AnalyticsTableMetricsEnum.TEAM_CHANGES,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-team-changes-table-title',
        message: 'Workspace',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-team-changes-table-tooltip-title">Workspace</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-team-changes-table-tooltip">
          Teammates with the greatest increases and decreases compared to the previous time period of the same
          length.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hz4mn/overview-report#team-list`,
      metricId: AnalyticsTableMetricsEnum.TEAM_CHANGES,
    },
  },
  [AnalyticsTableMetricsEnum.TOP_TAGS]: {
    id: AnalyticsTableMetricsEnum.TOP_TAGS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-top-tags-table-title',
        message: 'Top tags',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-top-tags-table-tooltip-title">Top tags</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-top-tags-table-tooltip">
          Tags most often associated with conversations during the period, with changes from the previous
          period of the same length.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hz4mn/overview-report#top-tags-list`,
      metricId: AnalyticsTableMetricsEnum.TOP_TAGS,
    },
  },
  [AnalyticsTableMetricsEnum.CONVERSATIONS_INBOX_TABLE]: {
    id: AnalyticsTableMetricsEnum.CONVERSATIONS_INBOX_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-inboxes-table-title',
        message: 'Inboxes',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-inboxes-table-tooltip-title">Inboxes</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-inboxes-table-tooltip">
          Key metrics displayed by inbox. You can customize metrics using the gear icon.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#inboxes-table`,
      metricId: AnalyticsTableMetricsEnum.CONVERSATIONS_INBOX_TABLE,
    },
    columns: validateColumnsTooltipType({
      open_at_start: {
        label: i18n._(
          defineMessage({
            id: 'analytics-open-at-start',
            message: 'Open at start',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-start-tooltip-title">Open at start</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-start-tooltip">
              Conversations that were already open at the start of the time period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlq5t`,
          metricId: 'open_at_start',
        },
      },
      active: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-active-label',
            message: 'Active conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-active-tooltip-title">Active conversations</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-active-tooltip">
              Conversation segments with at least one activity during this period (tag, comment, assign, ...).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf`,
          metricId: 'active',
        },
      },
      new_direct_in_inbox: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-new-direct-in-inbox-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: 'new_direct_in_inbox',
        },
      },
      moved_into_inbox: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-moved-into-inbox-label',
            message: 'Moved into inbox',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-moved-into-inbox-tooltip-title">
              Moved into inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-moved-into-inbox-tooltip">
              Conversations moved into the inbox from another inbox.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqg0`,
          metricId: 'moved_into_inbox',
        },
      },
      reopened: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-reopened-label',
            message: 'Reopened',
          }),
        ),
        tooltip: {
          title: <Trans id="analytics-metric-inbox-table-columns-reopened-tooltip-title">Reopened</Trans>,
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-reopened-tooltip">
              Conversations reopened this period that were archived at the start of the period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlqr3`,
          metricId: 'reopened',
        },
      },
      archived_in_inbox: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-archived-in-inbox-label',
            message: 'Archived in inbox',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-tooltip-title">
              Archived in inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-tooltip">
              Number of conversations segments archived or snoozed during this period that remained archived
              at the end of the period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf`,
          metricId: 'archived_in_inbox',
        },
      },
      archived_in_inbox_without_reply: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-label',
            message: 'Archived without reply',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-tooltip-title">
              Archived without reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-tooltip">
              Conversations that were archived with no reply in the past sent by your teammates.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qb/archived-without-reply`,
          metricId: 'archived_in_inbox_without_reply',
        },
      },
      archived_in_inbox_with_reply: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-label',
            message: 'Archived with reply',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one reply.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7`,
          metricId: 'archived_in_inbox_with_reply',
        },
      },
      moved_out_of_inbox: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-moved-out-of-inbox-label',
            message: 'Moved out of inbox',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-moved-out-of-inbox-tooltip-title">
              Moved out of inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-moved-out-of-inbox-tooltip">
              Conversations moved out of the inbox into another inbox.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqjh`,
          metricId: 'moved_out_of_inbox',
        },
      },
      open_at_end: {
        label: i18n._(
          defineMessage({
            id: 'analytics-open-at-end',
            message: 'Open at end',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-end-tooltip-title">Open at end</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-end-tooltip">
              Conversations still open at the end of the period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqjs`,
          metricId: 'open_at_end',
        },
      },
      messages_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-messages-sent-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: 'messages_sent',
        },
      },
      messages_received: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-inbox-table-columns-messages-received-label',
            message: 'Messages received',
          }),
        ),
        tooltip: {
          ...messagesReceivedTooltip,
          metricId: 'messages_received',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE]: {
    id: AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-inboxes-table-title',
        message: 'Workload by inbox',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-workload-by-inboxes-table-tooltip-title">Workload by inbox</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-workload-by-inboxes-table-tooltip">
          Compare volume and efficiency metrics by inboxes to identify outliers.
        </Trans>
      ),
      learnMoreUrl: `${workloadReportHelpCenterUrl}#workload_by_inbox_table`,
      metricId: AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE,
    },
    columns: validateColumnsTooltipType({
      [AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-num-new-segments-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
        },
      },
      [AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-num-unresolved-active-segments-label',
            message: 'Unresolved conversations',
          }),
        ),
        tooltip: {
          ...stageUnresolvedTooltip,
          metricId: AnalyticsScalarMetricsEnum.STAGE_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
        },
      },
      [AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-avg-resolution-time-label',
            message: 'Resolution time (avg)',
          }),
        ),
        tooltip: {
          ...stageResolutionTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-response-time-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.MESSAGES_SENT]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-messages-sent-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
        },
      },
      [AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-messages-received-label',
            message: 'Messages received',
          }),
        ),
        tooltip: {
          ...messagesReceivedTooltip,
          metricId: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
        },
      },
      [AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-resolved-segments',
            message: 'Resolved conversations',
          }),
        ),
        tooltip: {
          ...stageResolvedTooltip,
          metricId: AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS,
        },
      },
      [AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-replies-to-resolution',
            message: 'Replies to resolution (avg)',
          }),
        ),
        tooltip: {
          ...stageRepliesToResolutionTooltip,
          metricId: AnalyticsScalarMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION,
        },
      },
      [AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-pct-resolved-on-first-reply',
            message: 'Resolved on first reply (%)',
          }),
        ),
        tooltip: {
          ...stageResolvedFirstReplyTooltip,
          metricId: AnalyticsScalarMetricsEnum.STAGE_PCT_RESOLVED_ON_FIRST_REPLY,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-first-reply-time',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'nalytics-metric-workload-by-inboxes-table-columns-avg-handle-time',
            message: 'Handle time (avg)',
          }),
        ),
        tooltip: {
          ...handleTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE_WITH_TICKETING]: {
    id: AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE_WITH_TICKETING,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-workload-by-inboxes-table-title',
        message: 'Workload by inbox',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-workload-by-inboxes-table-tooltip-title">Workload by inbox</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-workload-by-inboxes-table-tooltip">
          Compare volume and efficiency metrics by inboxes to identify outliers.
        </Trans>
      ),
      learnMoreUrl: `${workloadReportHelpCenterUrl}#workload_by_inbox_table`,
      metricId: AnalyticsTableMetricsEnum.WORKLOAD_INBOX_TABLE_WITH_TICKETING,
    },
    columns: validateColumnsTooltipType({
      [AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-num-new-segments-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
        },
      },
      [AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-num-unresolved-active-segments-label',
            message: 'Unresolved conversations',
          }),
        ),
        tooltip: {
          ...ticketUnresolvedTooltip,
          metricId: AnalyticsScalarMetricsEnum.TICKET_NUM_UNRESOLVED_ACTIVE_SEGMENTS,
        },
      },
      [AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-avg-resolution-time-label',
            message: 'Resolution time (avg)',
          }),
        ),
        tooltip: {
          ...ticketResolutionTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.TICKET_AVG_RESOLUTION_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-response-time-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.MESSAGES_SENT]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-messages-sent-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
        },
      },
      [AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-messages-received-label',
            message: 'Messages received',
          }),
        ),
        tooltip: {
          ...messagesReceivedTooltip,
          metricId: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
        },
      },
      [AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-resolved-segments',
            message: 'Resolved conversations',
          }),
        ),
        tooltip: {
          ...ticketResolvedTooltip,
          metricId: AnalyticsScalarMetricsEnum.TICKET_NUM_RESOLVED_SEGMENTS,
        },
      },
      [AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-replies-to-resolution',
            message: 'Replies to resolution (avg)',
          }),
        ),
        tooltip: {
          ...ticketRepliesToResolutionTooltip,
          metricId: AnalyticsScalarMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION,
        },
      },
      [AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-stage-pct-resolved-on-first-reply',
            message: 'Resolved on first reply (%)',
          }),
        ),
        tooltip: {
          ...ticketResolvedFirstReplyTooltip,
          metricId: AnalyticsScalarMetricsEnum.TICKET_PCT_RESOLVED_ON_FIRST_REPLY,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-workload-by-inboxes-table-columns-avg-first-reply-time',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
        },
      },
      [AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME]: {
        label: i18n._(
          defineMessage({
            id: 'nalytics-metric-workload-by-inboxes-table-columns-avg-handle-time',
            message: 'Handle time (avg)',
          }),
        ),
        tooltip: {
          ...handleTimeTooltip,
          metricId: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE]: {
    id: AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-message-template-table-title',
        message: 'Message template details',
      }),
    ),
    tooltip: {
      title: <Trans id="tags-report-message-template-table-tooltip-title">Message template details</Trans>,
      tooltipContent: (
        <Trans id="tags-report-message-template-table-tooltip">Key metrics per message template used.</Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/y4hg2md/message-templates-report`,
      metricId: AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE,
    },
    columns: validateColumnsTooltipType({
      num_messages_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-message-template-table-num-messages-sent-column-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: 'num_messages_sent',
        },
      },
      num_replies_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-message-template-table-num-replies-sent-column-label',
            message: 'Replies sent',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-message-template-table-num-replies-sent-column-tooltip-title">
              Replies sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-num-replies-sent-column-tooltip">
              Outbound messages sent by your teammates in reply to an inbound message.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqr8/replies-sent`,
          metricId: 'num_replies_sent',
        },
      },
      avg_handle_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-message-template-table-avg-handle-time-column-label',
            message: 'Handle time (avg)',
          }),
        ),
        tooltip: {
          ...handleTimeTooltip,
          metricId: 'avg_handle_time',
        },
      },
      customer_reply_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-message-template-table-customer-reply-rate-column-label',
            message: 'Customer reply rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-message-template-table-customer-reply-rate-column-tooltip-title">
              Customer reply rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-customer-reply-rate-column-tooltip">
              Percentages of messages sent that received an inbound reply from the customer.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hgmhb/customer-reply-rate`,
          metricId: 'customer_reply_rate',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.TAGS_ALL_TABLE]: {
    id: AnalyticsTableMetricsEnum.TAGS_ALL_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-tags-all-table-title',
        message: 'Tag details',
      }),
    ),
    tooltip: {
      title: <Trans id="tags-report-tags-all-table-tooltip-title">Tag details</Trans>,
      tooltipContent: (
        <Trans id="tags-report-tags-all-table-tooltip">
          Key metrics by tag during the selected time period. You can customize the metrics by clicking on the
          gear icon. Learn more.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hz4mc#tag-details-table`,
      metricId: AnalyticsTableMetricsEnum.TAGS_ALL_TABLE,
    },
    columns: validateColumnsTooltipType({
      num_active_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-active-segments-column-label',
            message: 'Conversations tagged',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-active-segments-column-tooltip-title">
              Conversations tagged
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-active-segments-column-tooltip">
              Conversations with this tag and at least one activity (message, tag added, etc) this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqk9/conversations-tagged`,
          metricId: 'num_active_segments',
        },
      },
      pct_active_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-pct-active-segments-column-label',
            message: 'Conversations tagged (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-pct-active-segments-column-tooltip-title">
              Conversations tagged (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-pct-active-segments-column-tooltip">
              Percent of conversation segments with this tag and at least one activity (tag, comment, assign,
              ...).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hlq18/conversations-tagged`,
          metricId: 'pct_active_segments',
        },
      },
      avg_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-avg-reply-time-column-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: 'avg_reply_time',
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-avg-first-reply-time-column-label',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: 'avg_first_reply_time',
        },
      },
      avg_reaction_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-avg-reaction-time-column-label',
            message: 'Reaction time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-avg-reaction-time-column-tooltip-title">
              Reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-avg-reaction-time-column-tooltip">
              Time from receiving a message to an assign, reply or archive action (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlq57/reaction-time-avg`,
          metricId: 'avg_reaction_time',
        },
      },
      avg_handle_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-avg-handle-time-column-label',
            message: 'Handle time (avg)',
          }),
        ),
        tooltip: {
          ...handleTimeTooltip,
          metricId: 'avg_handle_time',
        },
      },
      num_messages_received: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-messages-received-column-label',
            message: 'Messages received',
          }),
        ),
        tooltip: {
          ...messagesReceivedTooltip,
          metricId: 'num_messages_received',
        },
      },
      num_messages_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-messages-sent-column-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: 'num_messages_sent',
        },
      },
      num_new_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-new-segments-column-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: 'num_new_segments',
        },
      },
      num_archived_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-archived-segments-column-label',
            message: 'Archived conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-column-tooltip">
              Conversation segments archived or snoozed this period, remaining archived at the end of the
              period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
          metricId: 'num_archived_segments',
        },
      },
      num_archived_segments_with_reply: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-all-table-num-archived-segments-with-reply-column-label',
            message: 'Archived with reply',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-with-reply-column-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-with-reply-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one prior reply.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
          metricId: 'num_archived_segments_with_reply',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO]: {
    id: AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-stage-resolution-time-histo-title',
        message: 'Resolution time',
      }),
    ),
    tooltip: {
      ...stageResolutionTimeTooltip,
      metricId: AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO,
    },
  },
  [AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO]: {
    id: AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-stage-replies-to-resolution-histo-title',
        message: 'Replies to resolution',
      }),
    ),
    tooltip: {
      ...stageRepliesToResolutionTooltip,
      metricId: AnalyticsTableMetricsEnum.STAGE_AVG_REPLIES_TO_RESOLUTION_HISTO,
    },
  },
  [AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO]: {
    id: AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-ticket-resolution-time-histo-title',
        message: 'Resolution time',
      }),
    ),
    tooltip: {
      ...ticketResolutionTimeTooltip,
      metricId: AnalyticsTableMetricsEnum.TICKET_AVG_RESOLUTION_TIME_HISTO,
    },
  },
  [AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO]: {
    id: AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-ticket-replies-to-resolution-histo-title',
        message: 'Replies to resolution',
      }),
    ),
    tooltip: {
      ...ticketRepliesToResolutionTooltip,
      metricId: AnalyticsTableMetricsEnum.TICKET_AVG_REPLIES_TO_RESOLUTION_HISTO,
    },
  },
  [AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM]: {
    id: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-reply-time-histo-title',
        message: 'Reply time',
      }),
    ),
    tooltip: {
      ...replyTimeTooltip,
      metricId: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
    },
  },
  [AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM]: {
    id: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-first-reply-time-histo-title',
        message: 'First reply time',
      }),
    ),
    tooltip: {
      ...firstReplyTimeTooltip,
      metricId: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
    },
  },
  [AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS]: {
    id: AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-tag-pct-conversations-title',
        message: 'Conversations with tags',
      }),
    ),
    tooltip: {
      title: <Trans id="tag-pct-conversations-tooltip-title">Conversations with tags</Trans>,
      tooltipContent: (
        <Trans id="tag-pct-conversations-tooltip">
          This table shows how tags were added and removed over the report period. If you use tags to capture
          internal processes, you will be able to see how those processes are performing.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h4244c/understanding-tags`,
      metricId: AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS,
    },
  },
  [AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS]: {
    id: AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-messages-pct-message-templates-title',
        message: 'Messages with templates',
      }),
    ),
    tooltip: {
      title: <Trans id="pct-message-templates-tooltip-title">Messages with templates</Trans>,
      tooltipContent: (
        <Trans id="pct-message-templates-tooltip">
          Percentage of outbound messages sent using a message template. Using message templates can help you
          reply faster to your customers.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/83hg2mb/messages-with-templates`,
      metricId: AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS,
    },
  },
  [AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE]: {
    id: AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metrics-tags-changes-table-title',
        message: 'Tag changes',
      }),
    ),
    tooltip: {
      title: <Trans id="tags-report-tags-changes-table-tooltip-title">Tag changes</Trans>,
      tooltipContent: (
        <Trans id="tags-report-tags-changes-table-tooltip">
          This table shows how tags were added and removed over the report period. If you use tags to capture
          internal processes, you will be able to see how those processes are performing.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hz4mc#tag-changes-table`,
      metricId: AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE,
    },
    columns: validateColumnsTooltipType({
      num_active_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-changes-table-num-active-segments-column-label',
            message: 'Conversations with tag changes',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-active-segments-column-tooltip-title">
              Conversations with tag changes
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-active-segments-column-tooltip">
              Conversations with at least one tag addition or removal this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlscr/conversations-with-tag-changes`,
          metricId: 'num_active_segments',
        },
      },
      num_added: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-changes-table-num-added-column-label',
            message: 'Added this period',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-added-column-tooltip-title">
              Added this period
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-added-column-tooltip">
              Number of times this tag was added this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/y4hlscw/added-this-period`,
          metricId: 'num_added',
        },
      },
      num_removed: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-changes-table-num-removed-column-label',
            message: 'Removed this period',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-removed-column-tooltip-title">
              Removed this period
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-removed-column-tooltip">
              Number of times this tag was removed this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hls4g/removed-this-period`,
          metricId: 'num_removed',
        },
      },
      avg_tag_duration: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-tags-changes-table-avg-tag-duration-column-label',
            message: 'Tag duration (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-avg-tag-duration-column-tooltip-title">
              Tag duration (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-avg-tag-duration-column-tooltip">
              Time from applying a tag to removing it (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hls4j/tag-duration-avg`,
          metricId: 'avg_tag_duration',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.TEAM_PERFORMANCE]: {
    id: AnalyticsTableMetricsEnum.TEAM_PERFORMANCE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-team-performance-table-label',
        message: 'Team performance',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-team-performance-table-tooltip-title">Team performance</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-team-performance-table-tooltip">
          The performance of individual teammates. You can customize the metrics by clicking on the gear icon.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/y4hz4m0/team-performance-report#teammates-table`,
      metricId: AnalyticsTableMetricsEnum.TEAM_PERFORMANCE,
    },
    columns: validateColumnsTooltipType({
      teammate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-teammate-column-label',
            message: 'Teammate',
          }),
        ),
      },
      avg_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-reply-time-column-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: 'avg_reply_time',
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-first-reply-time-column-label',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: 'avg_first_reply_time',
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-total-reply-time-column-label',
            message: 'Total reply time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-total-reply-time-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-total-time-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
          metricId: 'avg_total_reply_time',
        },
      },
      avg_handle_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-handle-time-column-label',
            message: 'Handle time (avg)',
          }),
        ),
        tooltip: {
          ...handleTimeTooltip,
          metricId: 'avg_handle_time',
        },
      },
      avg_resolution_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-resolution-time-column-label',
            message: 'Resolution Time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-resolution-time-column-tooltip-title">
              Resolution time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-resolution-time-column-tooltip">
              Time between first inbound and final archive of a conversation (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc/total-reply-time-avg`,
          metricId: 'avg_resolution_time',
        },
      },
      avg_reaction_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-reaction-time-column-label',
            message: 'Reaction time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-reaction-time-column-tooltip-title">
              Reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-reaction-time-column-tooltip">
              Time from receiving a message to an assign, reply or archive action (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlq57`,
          metricId: 'avg_reaction_time',
        },
      },
      avg_first_reaction_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-avg-first-reaction-time-column-label',
            message: 'First reaction time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-first-reaction-time-column-tooltip-title">
              First reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-first-reaction-time-column-tooltip">
              Time from receiving the first message in a conversation to the first assign, reply or archive
              action (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlq12`,
          metricId: 'avg_first_reaction_time',
        },
      },
      num_messages_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-messages-sent-column-label',
            message: 'Messages sent',
          }),
        ),
        tooltip: {
          ...messagesSentTooltip,
          metricId: 'num_messages_sent',
        },
      },
      num_replies_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-replies-sent-column-label',
            message: 'Replies sent',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-replies-sent-column-tooltip-title">
              Replies sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-replies-sent-column-tooltip">
              Outbound messages sent by your teammates in reply to an inbound message.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqr8/replies-sent`,
          metricId: 'num_replies_sent',
        },
      },
      num_non_replies_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-label',
            message: 'Messages sent (excluding replies)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-tooltip-title">
              Messages sent (excluding replies)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-tooltip">
              Outbound messages sent by your teammates that were not replies to an inbound message.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqgn`,
          metricId: 'num_non_replies_sent',
        },
      },
      num_archived_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-archived-segments-column-label',
            message: 'Archived conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-column-tooltip">
              Conversation segments archived or snoozed this period, remaining archived at the end of the
              period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf`,
          metricId: 'num_archived_segments',
        },
      },
      num_assigned_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-assigned-segments-column-label',
            message: 'Assigned conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-assigned-segments-column-tooltip-title">
              Assigned conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-assigned-segments-column-tooltip">
              Conversations with activity this period while this teammate was the assignee.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/y4hz9qz`,
          metricId: 'num_assigned_segments',
        },
      },
      num_touched_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-touched-segments-column-label',
            message: 'Touched conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-touched-segments-column-tooltip-title">
              Touched conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-touched-segments-column-tooltip">
              Conversations with an action taken by this teammate (applied a tag, sent a message, commented,
              etc).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlq28`,
          metricId: 'num_touched_segments',
        },
      },
      num_archived_segments_with_reply: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-archived-segments-with-reply-column-label',
            message: 'Archived with reply',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-with-reply-column-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-with-reply-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one reply.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7`,
          metricId: 'num_archived_segments_with_reply',
        },
      },
      num_sla_breaches: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-sla-breaches-column-label',
            message: 'SLA breaches',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-sla-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-sla-breaches-column-tooltip">
              Number of times you missed your SLA commitment by not replying to a message in time.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58`,
          metricId: 'num_sla_breaches',
        },
      },
      num_msg_templates_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-num-msg-templates-sent-column-label',
            message: 'Message templates sent',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-msg-templates-sent-column-tooltip-title">
              Message templates sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-msg-templates-sent-column-tooltip">
              Number of messages sent that used a message template.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hgmhd/message-templates-sent`,
          metricId: 'num_msg_templates_sent',
        },
      },
      avg_csat_survey_score: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-avg-csat-survey-score-column-label',
            message: 'Satisfaction score (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-csat-survey-score-tooltip-title">
              Satisfaction score (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-csat-survey-score-column-tooltip">
              Average score of survey responses this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/2407`,
          metricId: 'avg_csat_survey_score',
        },
      },
      csat_survey_satisfaction: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-team-performance-table-csat-survey-satisfaction-column-label',
            message: 'Satisfaction score (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-csat-survey-satisfaction-column-tooltip-title">
              Satisfaction score (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-csat-survey-satisfaction-column-tooltip">
              Percent of survey responses this period with 4 or 5 stars.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
          metricId: 'csat_survey_satisfaction',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2]: {
    id: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-sla-status-table-v2-label',
        message: 'SLA details',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-sla-status-table-v2-tooltip-title">SLA details</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-sla-status-table-v2-tooltip">
          Performance metrics for all of your existing SLA Rules for the selected time period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hh1bw/sla-rules`,
      metricId: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2,
    },
    columns: validateColumnsTooltipType({
      sla_rule: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-v2-sla-rule-column-label',
            message: 'SLA rule',
          }),
        ),
      },
      sla_time_goal: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-time-goal-column-label',
            message: 'SLA goal',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-time-goal-column-tooltip-title">
              SLA goal
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-time-goal-column-tooltip">
              SLA time limit defined in this rule.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hz4m7/slas-reports`,
          metricId: 'sla_time_goal',
        },
      },
      avg_sla_breach_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-label',
            message: 'SLA breach time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-tooltip-title">
              SLA breach time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-tooltip">
              Time from the SLA breach to the next reply (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hlqrj/sla-breach-time-avg`,
          metricId: 'avg_sla_breach_time',
        },
      },
      pct_sla_achieved: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-achieved-column-label',
            message: 'SLA achievement (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-achieved-column-tooltip-title">
              SLA achievement (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-achieved-column-tooltip">
              Percentage of messages with SLA conditions that did not end up in breach.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
          metricId: 'pct_sla_achieved',
        },
      },
      sla_warnings: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-warning-column-label',
            message: 'SLA warnings',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-warning-column-tooltip-title">
              SLA warnings
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-warning-column-tooltip">
              Number of times an SLA warning tag was applied by this rule.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
          metricId: 'sla_warnings',
        },
      },
      sla_applies: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-applies-column-label',
            message: 'Messages with SLA',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-applies-column-tooltip-title">
              Messages with SLA
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-applies-column-tooltip">
              Number of messages that met SLA conditions this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hk47r`,
          metricId: 'sla_applies',
        },
      },
      sla_breaches: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-sla-v2-breaches-column-label',
            message: 'SLA breaches',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-breaches-column-tooltip">
              Number of times you missed your SLA commitment by not replying to a message in time.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
          metricId: 'sla_breaches',
        },
      },
      num_current_breach: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-sla-status-table-v2-num-current-breach-column-label',
            message: 'Conversations in breach at end',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-v2-num-current-breach-column-tooltip-title">
              Conversations in breach at end
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-v2-num-current-breach-column-tooltip">
              Conversations still in SLA breach at the end of the period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlhwg/conversations-in-breach-at-end`,
          metricId: 'num_current_breach',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.ACCOUNTS_TABLE]: {
    id: AnalyticsTableMetricsEnum.ACCOUNTS_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-accounts-table-label',
        message: 'Accounts',
      }),
    ),
    columns: validateColumnsTooltipType({
      num_active_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-num-active-segments-column-label',
            message: 'Active conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-active-segments-column-tooltip-title">
              Active conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-active-segments-column-tooltip">
              Conversation segments with at least one activity during this period (tag, comment, assign, ...).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
          metricId: 'num_active_segments',
        },
      },
      num_new_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-num-new-segments-column-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: 'num_new_segments',
        },
      },
      num_archived_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-num-archived-segments-column-label',
            message: 'Archived conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-archived-segments-column-tooltip">
              Conversation segments archived or snoozed this period, remaining archived at the end of the
              period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
          metricId: 'num_archived_segments',
        },
      },
      num_received: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-num-received-column-label',
            message: 'Messages received from',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-received-column-tooltip-title">
              Messages received from
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-received-column-tooltip">
              Number of inbound messages received from your customers.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv`,
          metricId: 'num_received',
        },
      },
      num_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-num-sent-column-label',
            message: 'Messages sent to',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-sent-column-tooltip-title">Messages sent to</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-sent-column-tooltip">
              Number of outbound messages sent by your teammates.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
          metricId: 'num_sent',
        },
      },
      avg_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-avg-response-column-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: 'avg_reply_time',
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-avg-resolution-column-label',
            message: 'Total reply time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-resolution-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-resolution-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
          metricId: 'avg_total_reply_time',
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-avg-first-response-column-label',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: 'avg_first_reply_time',
        },
      },
      sla_breaches: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-sla-breaches-column-label',
            message: 'SLA breaches',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-sla-breaches-column-tooltip-title">SLA breaches</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-sla-breaches-column-tooltip">
              Number of times you missed your SLA commitment by not replying to a message in time.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
          metricId: 'sla_breaches',
        },
      },
      avg_csat_survey_score: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-avg-csat-survey-score-column-label',
            message: 'Satisfaction score (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-csat-survey-score-tooltip-title">
              Satisfaction score (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-csat-survey-score-column-tooltip">
              Average score of survey responses this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/2407`,
          metricId: 'avg_csat_survey_score',
        },
      },
      pct_csat_survey_satisfaction: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-accounts-table-pct-csat-survey-satisfaction-column-label',
            message: 'Satisfaction score (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-pct-csat-survey-satisfaction-column-tooltip-title">
              Satisfaction score (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-pct-csat-survey-satisfaction-column-tooltip">
              Percent of survey responses this period with 4 or 5 stars.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
          metricId: 'pct_csat_survey_satisfaction',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2]: {
    id: AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-customers-table-v2-label',
        message: 'Contacts',
      }),
    ),
    columns: validateColumnsTooltipType({
      num_active_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-num-active-segments-column-label',
            message: 'Active conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-active-segments-column-tooltip-title">
              Active conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-active-segments-column-tooltip">
              Conversation segments with at least one activity during this period (tag, comment, assign, ...).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
          metricId: 'num_active_segments',
        },
      },
      num_new_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-num-new-segments-column-label',
            message: 'New conversations',
          }),
        ),
        tooltip: {
          ...newConversationsTooltip,
          metricId: 'num_new_segments',
        },
      },
      num_archived_segments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-num-archived-segments-column-label',
            message: 'Archived conversations',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-archived-segments-column-tooltip">
              Conversation segments archived or snoozed this period, remaining archived at the end of the
              period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
          metricId: 'num_archived_segments',
        },
      },
      num_received: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-num-received-column-label',
            message: 'Messages received from',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-received-column-tooltip-title">
              Messages received from
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-received-column-tooltip">
              Number of inbound messages received from your customers.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv`,
          metricId: 'num_received',
        },
      },
      num_sent: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-num-sent-column-label',
            message: 'Messages sent to',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-sent-column-tooltip-title">
              Messages sent to
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-sent-column-tooltip">
              Number of outbound messages sent by your teammates.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
          metricId: 'num_sent',
        },
      },
      avg_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-avg-response-column-label',
            message: 'Reply time (avg)',
          }),
        ),
        tooltip: {
          ...replyTimeTooltip,
          metricId: 'avg_reply_time',
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-avg-resolution-column-label',
            message: 'Total reply time (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-avg-resolution-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-avg-resolution-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
          metricId: 'avg_total_reply_time',
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-avg-first-response-column-label',
            message: 'First reply time (avg)',
          }),
        ),
        tooltip: {
          ...firstReplyTimeTooltip,
          metricId: 'avg_first_reply_time',
        },
      },
      sla_breaches: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-sla-breaches-column-label',
            message: 'SLA breaches',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-sla-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-sla-breaches-column-tooltip">
              Number of times you missed your SLA commitment by not replying to a message in time.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
          metricId: 'sla_breaches',
        },
      },
      avg_csat_survey_score: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-customers-table-v2-avg-csat-survey-score-column-label',
            message: 'Satisfaction score (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-csat-survey-score-column-tooltip-title">
              Satisfaction score (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-avg-csat-survey-score-column-tooltip">
              Average score of survey responses this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/2407`,
          metricId: 'avg_csat_survey_score',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE]: {
    id: AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-sample-feedback-table-label',
        message: 'Sample Feedback',
      }),
    ),
  },

  // Sankey
  [AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-conversation-flows-label',
        message: 'Conversation flows',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-conversation-flows-tooltip-title">Conversation flows</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-conversation-flows-tooltip">
          Status changes of conversations during your selected time range.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#conversation-flows-chart`,
      metricId: AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH,
    },
  },

  [AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE]: {
    id: AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-chatbot-performance-table-title',
        message: 'Chatbot performance',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-chatbot-performance-table-tooltip-title">Chatbot performance</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-chatbot-performance-table-tooltip">
          Understand how Front Chat chatbots are performing. This data pertains only to chatbot interactions
          prior to the creation of the chat conversation. To measure how your team interacts with chat
          conversations, use the other reports.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/682560`,
      metricId: AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE,
    },
    columns: validateColumnsTooltipType({
      flows_initiated: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-flows-initiated',
            message: 'Flows initiated',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-flows-initiated-tooltip-title">
              Flows initiated
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-flows-initiated-tooltip">
              Number of times chat visitors start your chatbot flow.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684288`,
          metricId: 'flows_initiated',
        },
      },
      flows_completed: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-table-columns-flows-completed-label',
            message: 'Flows completed',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-flows-completed-tooltip-title">
              Flows completed
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-flows-completed-tooltip">
              Number of times chat visitors reach the end of a chatbot path.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684352`,
          metricId: 'flows_completed',
        },
      },
      chats_deflected: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-chats-deflected',
            message: 'Chats deflected',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-chats-deflected-tooltip-title">
              Chats deflected
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-chats-deflected-tooltip">
              Number of chatbot conversations ended and archived by your chatbot.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684416`,
          metricId: 'chats_deflected',
        },
      },
      deflection_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-deflection-rate',
            message: 'Deflection rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-deflection-rate-tooltip-title">
              Deflection rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-deflection-rate-tooltip">
              Percent of chatbot conversations ended and archived by your chatbot out of all completed chatbot
              conversations.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684480`,
          metricId: 'deflection_rate',
        },
      },
      drop_off_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-table-columns-dropoff-rate-label',
            message: 'Dropoff rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-dropoff-rate-tooltip-title">
              Dropoff rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-dropoff-rate-tooltip">
              Percent of chat visitors who don’t reach the end of a chatbot path out of the number of visitors
              who begin interacting with your chatbot flow.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684544`,
          metricId: 'drop_off_rate',
        },
      },
      time_to_completion: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-performance-time-to-completion',
            message: 'Time to completion (avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-time-to-completion-tooltip-title">
              Time to completion (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-performance-table-columns-time-to-completion-tooltip">
              Average time it takes for a chat visitor to reach the end of a chatbot path.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684608`,
          metricId: 'time_to_completion',
        },
      },
    }),
  },

  [AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE]: {
    id: AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-chatbot-ai-answers-table-title',
        message: 'AI Answers',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-chatbot-ai-answers-table-tooltip-title">AI Answers</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-chatbot-ai-answers-table-tooltip">
          Understand how AI Answers performs in your chatbot and how effectively it resolves inquiries.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/682560`,
      metricId: AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE,
    },
    columns: validateColumnsTooltipType({
      ai_participation_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-participation-rate',
            message: 'AI participation rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-participation-rate-tooltip-title">
              AI participation rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-participation-rate-tooltip">
              Percent of chatbot conversations where AI provided an answer out of all completed chatbot
              conversations.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_participation_rate',
        },
      },
      ai_reached_count: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-reached-count',
            message: 'Reached AI Answers step',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-reached-count-tooltip-title">
              Reached AI Answers step
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-answer-count-tooltip">
              Number of chatbot conversations where a chat visitor reached an “AI Answers” step.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_reached_count',
        },
      },
      ai_answer_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-answer-rate',
            message: 'AI answer rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-answer-rate-tooltip-title">
              AI answer rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-answer-rate-tooltip">
              Percent of chatbot conversations where AI provided an answer out of all conversations where chat
              visitor reached an “AI Answers” step.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_answer_rate',
        },
      },
      ai_answer_count: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-answer-count',
            message: 'AI answers',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-answer-count-tooltip-title">
              AI answers
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-answer-count-tooltip">
              Number of chatbot conversations where a chat visitor reached an “AI Answers” step and AI
              provided an answer.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_answer_count',
        },
      },
      ai_success_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-success-rate',
            message: 'AI success rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-success-rate-tooltip-title">
              AI success rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-success-rate-tooltip">
              Percent of chatbot conversations where the chat visitor indicated AI&apos;s response was helpful
              out of all conversations where the chat visitor provided feedback on AI&apos;s answer.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_success_rate',
        },
      },
      ai_resolution_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-resolution-rate',
            message: 'AI resolution rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-resolution-rate-tooltip-title">
              AI resolution rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-resolution-rate-tooltip">
              Percent of chatbot conversations where the chat visitor either indicated the AI’s answer was
              helpful or dropped from the chatbot flow after AI answered out of the number of chatbot
              conversations where AI provided an answer.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_resolution_rate',
        },
      },
      ai_resolution_count: {
        label: i18n._(
          defineMessage({
            id: 'analytics-metric-chatbot-ai-answers-ai-resolution-count',
            message: 'AI resolutions',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-resolution-count-tooltip-title">
              AI resolutions
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-chatbot-ai-answers-table-columns-ai-resolution-count-tooltip">
              Number of chatbot conversations where the chat visitor either indicated the AI’s answer was
              helpful or dropped from the chatbot flow after AI answered.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/684864`,
          metricId: 'ai_resolution_count',
        },
      },
    }),
  },

  [AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_VIEWS]: {
    id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-overview-total-views',
        message: 'Total views',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-knowledge-base-overview-total-views">Total views</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-content-knowledge-base-overview-total-views">
          Number of total page views across all pages.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/719872`,
      metricId: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_VIEWS,
    },
  },
  [AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_UNIQUE_VIEWS]: {
    id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_UNIQUE_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-overview-total-unique-views',
        message: 'Unique visitors',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-knowledge-base-overview-total-unique-views">
          Unique visitors
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-content-knowledge-base-overview-total-unique-views">
          Number of visitors across all pages. Each visitor is counted only once, regardless of repeated
          visits.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/719936`,
      metricId: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_UNIQUE_VIEWS,
    },
  },
  [AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_ARTICLE_VIEWS]: {
    id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_ARTICLE_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-overview-total-article-views',
        message: 'Articles viewed',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-knowledge-base-overview-total-article-views">
          Articles viewed
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-content-knowledge-base-overview-total-article-views">
          Number of articles viewed.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/720000`,
      metricId: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_ARTICLE_VIEWS,
    },
  },
  [AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES]: {
    id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-overview-total-articles-updated',
        message: 'Articles updated',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-knowledge-base-overview-total-articles-updated">
          Articles updated
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-content-knowledge-base-overview-total-articles-updated">
          Number of articles that got updated, and new ones that got published.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/720064`,
      metricId: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES,
    },
  },
  [AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_TOTAL_VIEWS]: {
    id: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_TOTAL_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-total-views-graph-label',
        message: 'Total views',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-metric-knowledge-base-total-views-graph-tooltip-title">Total views</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-knowledge-base-total-views-graph-tooltip-content">
          Number of total page views across all pages.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/719872`,
      metricId: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_TOTAL_VIEWS,
    },
  },
  [AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_VIEWS]: {
    id: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-total-unique-views-graph-label',
        message: 'Unique visitors',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-knowledge-base-total-unique-views-graph-tooltip-title">
          Unique visitors
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-knowledge-base-total-unique-views-graph-tooltip-content">
          Number of unique visitors across all pages.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/719936`,
      metricId: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_VIEWS,
    },
  },
  [AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_ARTICLE_VIEWS]: {
    id: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_ARTICLE_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-total-article-views-graph-label',
        message: 'Articles viewed',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-knowledge-base-total-article-views-graph-tooltip-title">
          Articles viewed
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-knowledge-base-total-article-views-graph-tooltip-content">
          Number of articles viewed.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/720000`,
      metricId: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_TOTAL_UNIQUE_ARTICLE_VIEWS,
    },
  },
  [AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_ARTICLE_UPDATES]: {
    id: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_ARTICLE_UPDATES,
    label: i18n._(
      defineMessage({
        id: 'analytics-metric-knowledge-base-total-article-views-graph-label',
        message: 'Articles updated',
      }),
    ),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-knowledge-base-total-article-views-graph-label">
          Articles updated
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-content-knowledge-base-total-article-views-graph-label">
          Number of articles that got updated, and new ones that got published.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/720064`,
      metricId: AnalyticsGraphMetricsEnum.PAGE_VIEW_NUM_ARTICLE_UPDATES,
    },
  },
  [AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS]: {
    id: AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS,
    label: i18n._(
      defineMessage({
        id: 'analytics-knowledge-base-engagement-table-title',
        message: 'Engagement',
      }),
    ),
    columns: validateColumnsTooltipType({
      page_name: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-page-name-column-title',
            message: 'Page name',
          }),
        ),
      },
      num_likes: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-num-likes-column-title',
            message: 'Helpful',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-engagement-table-num-likes-column-tooltip-title">
              Helpful
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-engagement-table-num-likes-column-tooltip-content">
              Number of times an article is marked as helpful by a visitor.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720192`,
          metricId: 'num_likes',
        },
      },
      num_dislikes: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-num-dislikes-column-title',
            message: 'Unhelpful',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-engagement-table-num-dislikes-column-tooltip-title">
              Unhelpful
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-engagement-table-num-dislikes-column-tooltip-content">
              Number of times an article is marked as unhelpful by a visitor.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720192`,
          metricId: 'num_dislikes',
        },
      },
      num_comments: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-num-comments-column-title',
            message: 'Comments',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-engagement-table-num-comments-column-tooltip-title">
              Comments
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-engagement-table-num-comments-column-tooltip-content">
              Number of comments left by a vistor on an article.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720192`,
          metricId: 'num_comments',
        },
      },
      page_view: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-page-views-column-title',
            message: 'Total views',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-engagement-table-page-views-column-tooltip-title">
              Views
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-engagement-table-page-views-column-tooltip-content">
              Number of times a page has been viewed including repeat visitors.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720128`,
          metricId: 'page_view',
        },
      },
      unique_page_view: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-engagement-table-unique-views-column-title',
            message: 'Unique views',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-engagement-table-unique-views-column-tooltip-title">
              Unique views
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-engagement-table-unique-views-column-content">
              Number of times a page has been viewed by unique visitors. If a visitor views it more than once,
              it will be only be counted once.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720192`,
          metricId: 'unique_page_view',
        },
      },
    }),
  },
  [AnalyticsTableMetricsEnum.SEARCH_TERMS]: {
    id: AnalyticsTableMetricsEnum.SEARCH_TERMS,
    label: i18n._(
      defineMessage({
        id: 'analytics-knowledge-base-search-terms-table-title',
        message: 'Search keyword detail',
      }),
    ),
    tooltip: {
      title: <Trans id="analytics-knowledge-base-search-table-tooltip-title">Search keyword details</Trans>,
      tooltipContent: (
        <Trans id="analytics-knowledge-base-search-table-tooltip-content">
          Detailed metrics for the keywords that visitors are searching for in the Knowledge Base.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/en/articles/696960`,
      metricId: AnalyticsTableMetricsEnum.SEARCH_TERMS,
    },
    columns: validateColumnsTooltipType({
      search_term: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-search-table-search-term-column-title',
            message: 'Keywords',
          }),
        ),
      },
      num_searches: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-search-table-num-searches-column-title',
            message: 'Total searches',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-search-table-num-searches-column-tooltip-title">
              Total searches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-search-table-num-searches-column-tooltip-content">
              Number of times the keyword has been used in a search.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720320`,
          metricId: 'num_searches',
        },
      },
      num_results: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-search-table-num-results-column-title',
            message: 'Search results (Avg)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-search-table-num-results-column-tooltip-title">
              Search results (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-search-table-num-results-column-content">
              Average count of pages shown for that keyword&apos;s search results.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720384`,
          metricId: 'num_results',
        },
      },
      click_through_rate: {
        label: i18n._(
          defineMessage({
            id: 'analytics-knowledge-base-search-table-click-through-rate-column-title',
            message: 'Click-through rate (%)',
          }),
        ),
        tooltip: {
          title: (
            <Trans id="analytics-knowledge-base-search-table-click-through-rate-column-tooltip-title">
              Click-through rate (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-knowledge-base-search-table-click-through-rate-column-tooltip-content">
              % of users who clicked on at least one result in the search out of total number of users who
              viewed those results.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/en/articles/720448`,
          metricId: 'click_through_rate',
        },
      },
    }),
  },
};

export const getMetricLabels = <T extends AnyMetricKey>(metric?: T): Partial<MetricWithLabel<T>> => {
  if (!metric) {
    return {};
  }

  const matchingLabel = metricsLabels[metric];

  if (!matchingLabel) {
    return {id: metric, label: undefined};
  }

  return matchingLabel;
};

const StyledLink = styled.a`
  font-weight: ${fontWeights.semibold};
  text-decoration: underline;
  color: ${(p) => p.theme.greys.white};
  cursor: pointer;
`;

export const maybeRenderFeaturePillAndTooltip = (
  featureStatus?: AnalyticsFeatureStatusesEnum,
  tooltip?: AnalyticsEducationTooltip,
  size = VisualSizesEnum.MEDIUM,
) => {
  if (!featureStatus && !tooltip) {
    return null;
  }

  const statusToPill = {
    [AnalyticsFeatureStatusesEnum.BETA]: BetaFeaturePill,
    [AnalyticsFeatureStatusesEnum.NEW]: NewFeaturePill,
    [AnalyticsFeatureStatusesEnum.LEGACY]: LegacyFeaturePill,
  };

  const PillComponent = isDefined(featureStatus) ? statusToPill[featureStatus] : undefined;

  return (
    <>
      {isComplexAnalyticsTooltip(tooltip) && (
        <TooltipCoordinator
          persistent
          render={() => (
            <>
              {tooltip.tooltipContent}&nbsp;
              <StyledLink href={tooltip.learnMoreUrl} target="_blank">
                <Trans id="analytics-metric-learn-more">Learn more</Trans>
              </StyledLink>
            </>
          )}
        >
          <TooltipIconWithHover />
        </TooltipCoordinator>
      )}
      {PillComponent && <PillComponent size={size} />}
    </>
  );
};
